import { FC } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import PostRequirementView from 'components/shared/detail/PostRequirementView';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import { usePostOverviewOptimisticQueryUpdater } from 'app/modules/posts/modules/post/hooks/usePostOverview';
import { Requirement } from 'types/Requirement';
import { queryKey } from 'constants/queryKey';

type Props = {
  requirement: Requirement;
  postId: number;
};

const PostRequirement: FC<Props> = ({ requirement, postId }) => {
  const queryClient = useQueryClient();
  const updater = usePostOverviewOptimisticQueryUpdater(postId);
  const toast = useToast();
  const mutation = useMutation(
    () => PostApi.toggleRequirement(postId, requirement.id),
    {
      async onMutate() {
        await queryClient.cancelQueries(queryKey.postDetail(postId));

        updater.update((prevPost) =>
          updater.transformers.requirementToggle(requirement.id, prevPost),
        );
      },
      onSettled() {
        updater.invalidateData();
      },
      onError() {
        updater.update((prevPost) =>
          updater.transformers.requirementToggle(requirement.id, prevPost),
        );
        toast('Unable to save post requirement state.', 'error');
      },
    },
  );

  return (
    <UserPermissionGate
      scopes={UserPermission.POST_TOGGLE_REQUIREMENTS}
      deniedProps={{ isDisabled: true }}
    >
      <PostRequirementView
        requirement={requirement}
        onClick={mutation.mutate}
        isLoading={mutation.isLoading}
      />
    </UserPermissionGate>
  );
};

export default PostRequirement;
